/* eslint-disable max-len */
import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AxiosResponse } from 'axios';

import { AirDropContext } from '../../../../utils/AirDropProvider';
import Button from '../../../atoms/Button/Button';

import { event } from '../../../../services';
import useTranslation from '../../../hooks/useTranslation';
import { getFile } from '../../../../utils/commonUtils.js';
import { getErrorMessageFromPiErrorCode, getPiErrorCodeFromAxiosError } from '../../../../utils/errorUtils';

import { getRightToInfoFileSvc } from '../../../../services/accountManagement';

import './AccountManagementTabs.scss';

const AccountManagementMainPage = React.lazy(() => import('../AcctMgmtMainPage'));

interface Policy {
  category: string
  url: string
}

interface UpdatedPolicy {
  url: string
  origUrl: string
  category: string
  tabId: string
  contentId: string
  isPearsonDotCom: boolean
}

function AccountManagementTabs() {
  const ANALYTICS_CATEGORY = 'Account Management';
  const ANALYTICS_EVENT_NAME = 'Select Tab';
  const config: any = useContext(AirDropContext);
  const { translateText } = useTranslation();
  const { state } = useLocation();
  const { loginRootUrl } = config;
  const [selectedTab, setSelectedTab] = useState('account_tab');

  const [policies, setPolicies] = useState([]);
  const [showReportTab, setShowReportTab] = useState(true);
  const [showDownloadError, setShowDownloadError] = useState(false);
  const [accountOrig, setAccountOrig] = useState<AccountData | any>(undefined);

  const selectTab = (tabId: string, label: string) => {
    setSelectedTab(tabId);
    event(
      ANALYTICS_EVENT_NAME,
      ANALYTICS_CATEGORY,
      'Link',
      `${ANALYTICS_CATEGORY} Tab ${label} selected`,
      config.CLIENT_ID,
    );
  };

  const updateAccountData = (accountData: AccountData) => {
    setAccountOrig(accountData);
    const rightToInfoUrl = accountData.rightToInfoUrl && accountData?.rightToInfoUrl.value;
    const shouldShowReportTab = !!rightToInfoUrl;
    setShowReportTab(shouldShowReportTab);
  };

  const getRightToInfoFileFailure = () => {
    setShowDownloadError(true);
  };

  const getRightToInfoFileSuccess = () => {
    setShowDownloadError(false);
  };

  const getDataPrivacyTokenSuccess = (response: AxiosResponse<DataPrivacyResponse>) => {
    const dpAccessToken = response?.data?.data;

    getFile(
      'Right to know request report.pdf',
      accountOrig?.rightToInfoUrl?.value?.url,
      dpAccessToken,
      getRightToInfoFileSuccess,
      getRightToInfoFileFailure,
    );
  };

  const getRightToInfoFile = async () => {
    setShowDownloadError(false);
    try {
      const resp = await getRightToInfoFileSvc(state?.authToken);
      getDataPrivacyTokenSuccess(resp);
    } catch (error) {
      const piErrorCode = getPiErrorCodeFromAxiosError(error);
      const piErrorMsg = getErrorMessageFromPiErrorCode(piErrorCode);
      event(ANALYTICS_EVENT_NAME, ANALYTICS_CATEGORY, 'Error', `${ANALYTICS_CATEGORY} ${piErrorMsg?.code}`);
      getRightToInfoFileFailure();
    }
  };

  useEffect(() => {
    const LOGIN_PATH = /^(\/login\/static\/html)/;
    const LOGIN_BASEURI = `${loginRootUrl}/login/static/html`;

    const newPolicies = config.consentPolicies.map(
      (policy: Policy, idx: any) => {
        const p: UpdatedPolicy = {
          url: '',
          origUrl: '',
          category: '',
          tabId: '',
          contentId: '',
          isPearsonDotCom: false,
        };
        p.url = policy.url.replace(LOGIN_PATH, LOGIN_BASEURI);
        p.origUrl = p.url;
        p.category = policy.category;
        p.tabId = `policy_tab_${idx}`;
        p.contentId = `policy_content_${idx}`;
        p.isPearsonDotCom = p.origUrl.indexOf('www.pearson.com') >= 0;
        return p;
      },
    );

    setPolicies(newPolicies);
  }, [config.consentPolicies]);

  return (
    <>
      <div className="acc-mgmnt-tabs-container">
        <Button
          aria-controls="account"
          aria-selected="true"
          id="account_tab"
          type="button"
          className={`ies-tabs ${selectedTab === 'account_tab' && 'active'}`}
          onClick={() => selectTab('account_tab', 'Account')}
        >
          {translateText('Account')}
        </Button>
        {policies?.map((policy: UpdatedPolicy) => (
          <Button
            key={policy?.tabId}
            type="button"
            id={policy?.tabId}
            role="tab"
            aria-controls={policy?.contentId}
            aria-selected="false"
            className={`ies-tabs ${selectedTab === policy?.tabId && 'active'}`}
            onClick={() => selectTab(policy?.tabId, policy?.category)}
          >
            {policy?.category}
          </Button>
        ))}
        {showReportTab && (
          <Button
            className={`ies-tabs ${selectedTab === 'report_tab' && 'active'}`}
            id="report_tab"
            role="tab"
            aria-controls="report"
            aria-selected="false"
            onClick={() => selectTab('report_tab', 'Information request')}
          >
            {translateText('Information Request')}
          </Button>
        )}
      </div>

      {selectedTab === 'account_tab' && (
        <div>
          <AccountManagementMainPage setAccountData={updateAccountData} />
        </div>
      )}

      {policies.map((policy: UpdatedPolicy) => (
        <div
          key={policy.contentId}
          id={policy.contentId}
          className="tab-content"
          role="tabpanel"
          aria-labelledby={policy.tabId}
        >
          {selectedTab === policy.tabId && (
            <>
              {!policy.isPearsonDotCom && (
                <iframe
                  src={policy.url}
                  title={translateText(policy.category)}
                />
              )}
              {policy.isPearsonDotCom && (
                <div className="centered">
                  {`External policy file could not be opened here. Use this link
                        to access the policy:`}
                  {'  '}
                  <a
                    href={policy.origUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={translateText('Opens in a new tab or window')}
                    className="privacy-link"
                  >
                    {translateText(policy.category)}
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      ))}

      {selectedTab === 'report_tab' && (
        <div
          id="report"
          className="tab-content"
          role="tabpanel"
          aria-labelledby="report_tab"
        >
          <div className="report-blurb">
            {translateText(
              'Download the PDF of your Right to Know request report.',
            )}
          </div>
          <div className="report-link">
            <div className="report-icon">
              <span className="sr-only">
                {translateText('download icon')}
              </span>
              <svg
                aria-hidden="true"
                focusable="false"
                className="pe-icon--download-18"
              >
                <use xlinkHref="#g-download-18" />
              </svg>
            </div>
            <Button
              className="pe-link"
              type="button"
              onClick={() => getRightToInfoFile()}
            >
              {translateText('Right to Know request report')}
            </Button>
          </div>
          <div
            className="pe-error-wrapper"
            aria-live="assertive"
            role="alert"
          >
            {showDownloadError && (
              <div className="pe-error-text">
                {translateText('An error was encountered downloading the report.')}
              </div>
            )}
          </div>
          <div style={{ display: 'none' }}>
            <svg xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
              <symbol id="g-download-18" viewBox="0 0 18 18">
                <path d="M15,12 C15,11.4477153 15.4477153,11 16,11 C16.5522847,11 17,11.4477153 17,12 L17,14.5 C17,15.8807119 15.8807119,17 14.5,17 L3.5,17 C2.11928813,17 1,15.8807119 1,14.5 L1,12 C1,11.4477153 1.44771525,11 2,11 C2.55228475,11 3,11.4477153 3,12 L3,14.5 C3,14.7761424 3.22385763,15 3.5,15 L14.5,15 C14.7761424,15 15,14.7761424 15,14.5 L15,12 Z" />
                <path d="M8,8.69922208 L8,1 C8,0.44771525 8.44771525,1.16477725e-14 9,1.15463195e-14 C9.55228475,1.14448664e-14 10,0.44771525 10,1 L10,8.70150263 L12.3972414,6.28004799 C12.7638946,5.9096914 13.3583569,5.9096914 13.7250101,6.28004799 C14.0916633,6.65040457 14.0916633,7.25087123 13.7250101,7.62122781 L9.66501324,11.7222326 C9.29836003,12.0925891 8.70389772,12.0925891 8.33724451,11.7222326 L4.27498991,7.61894726 C3.9083367,7.24859068 3.9083367,6.64812402 4.27498991,6.27776744 C4.64164312,5.90741085 5.23610543,5.90741085 5.60275864,6.27776744 L8,8.69922208 Z" />
              </symbol>
            </svg>
          </div>
        </div>
      )}
    </>
  );
}

export default AccountManagementTabs;
